import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";
import Button from "@material-ui/core/Button";

import { IAppState } from "../../../types/stores/index";
// import { IClientVariable } from "../../../types/stores/vars";
import MaterialTable, { Column } from "@material-table/core";
import { FieldMetaData } from "../../../types/stores/adminDesigner";
import NumericalTextField from "../../material-components/Misc/NumericalTextField";
import TextFieldForTable from "../../material-components/Misc/TextFieldForTable";

interface IFieldMetaDataMain {
    //metadata: Array<MappedFieldMetadata>;
    onChangeMetadata: (metadata: Array<FieldMetaData>) => void;
    fieldMetaDataFieldkey: number;
    occurs: number;
}

const FieldMetaDataMain: React.FC<IFieldMetaDataMain> = ({
    occurs,
    onChangeMetadata,
    fieldMetaDataFieldkey,
}: IFieldMetaDataMain) => {
    const dispatch = useDispatch();
    const [newMetaDataId, setNewMetaDataId] = useState(-1);
    const [localMetadata, setLocalMetadata] = useState<Array<FieldMetaData>>([]);

    const isSaving = useSelector<IAppState, boolean>(state => state.adminDesigner.isSaving);

    const selectedFields = useSelector<IAppState, Array<number>>(state => state.dataConnect.selectedFields || []);
    const selectedFieldMeta = useSelector<IAppState, Array<FieldMetaData>>(
        state => state.adminDesigner.fieldMetadata || []
    );

    useEffect(() => {
        setLocalMetadata(selectedFieldMeta);
    }, [dispatch, selectedFieldMeta]);

    const renderDeleteButton = (row: FieldMetaData) => (
        <Tooltip title={"Delete this metadata value"}>
            <div>
                <IconButton color="secondary" onClick={() => deleteMetaDataRow(row)} aria-label="delete metadata">
                    <i className="material-icons">delete</i>
                </IconButton>
            </div>
        </Tooltip>
    );

    const deleteMetaDataRow = (row: FieldMetaData) => {
        const newMetaData = [...localMetadata];
        const metadataIndex = localMetadata.findIndex(x => x.FieldMetaDataKey == row.FieldMetaDataKey);
        newMetaData.splice(metadataIndex, 1);
        setLocalMetadata(newMetaData);
        onChangeMetadata(newMetaData);
    };

    const sortMetaData = () => {
        let newMetadata = [...localMetadata];
        newMetadata = newMetadata.slice();
        newMetadata.sort((a, b) => a.FieldMetaDataEnglish.localeCompare(b.FieldMetaDataEnglish));
        for (let i = 0; i < newMetadata.length; i++) {
            newMetadata[i].FieldMetaSort = i + 1;
        }
        setLocalMetadata(newMetadata);
        onChangeMetadata(newMetadata);
    };

    const renderSortCell = (row: FieldMetaData) => (
        <NumericalTextField
            disabled={isSaving}
            value={row.FieldMetaSort}
            onBlur={e => {
                let newValue = parseInt(e.target.value);
                if (newValue < 1) {
                    newValue = 1;
                }
                const metadataIndex = localMetadata.findIndex(x => x.FieldMetaDataKey == row.FieldMetaDataKey);
                let newMetadata: Array<FieldMetaData> = [...localMetadata];
                newMetadata[metadataIndex].FieldMetaSort = newValue;
                setLocalMetadata(newMetadata);
                onChangeMetadata(newMetadata);
            }}
        />
    );

    const renderEditableString = (row: FieldMetaData, property: string) => (
        <TextFieldForTable
            disabled={isSaving || !selectedFields}
            value={row[property]}
            onBlur={e => {
                const newValue = e.target.value;
                const metadataIndex = localMetadata.findIndex(x => x.FieldMetaDataKey == row.FieldMetaDataKey);
                let newMetadata = [...localMetadata];
                newMetadata[metadataIndex][property] = newValue;
                setLocalMetadata(newMetadata);
                onChangeMetadata(newMetadata);
            }}
        />
    );

    const metadataColumns: Array<Column<FieldMetaData>> = [
        {
            title: "Value",
            field: "value",
            render: rowData => renderEditableString(rowData, "FieldMetaDataValue"),
        },
        {
            title: "Label",
            field: "english",
            render: rowData => renderEditableString(rowData, "FieldMetaDataEnglish"),
        },
        {
            title: "Sort",
            field: "sort",
            render: rowData => renderSortCell(rowData),
        },
        {
            title: "Delete",
            field: "english",
            render: rowData => renderDeleteButton(rowData),
        },
    ];

    const renderMetadataTable = () => (
        <MaterialTable
            title={"Metadata"}
            data={localMetadata}
            columns={metadataColumns}
            options={{
                toolbar: false,
                filtering: true,
                grouping: false,
                search: true,
                headerStyle: { fontSize: 12 },
                rowStyle: {
                    fontSize: 12,
                    padding: "2px",
                },
                pageSize: 10,
                pageSizeOptions: [10, 50, 75, 100],
            }}
        />
    );

    const addMetadataRow = () => {
        let newMetadata: Array<FieldMetaData> = [];
        if (localMetadata) {
            newMetadata = [...localMetadata];
        }
        newMetadata.push({
            FieldMetaDataKey: newMetaDataId,
            FieldMetaDataValue: "",
            FieldMetaDataEnglish: "",
            FieldMetaDataFieldKey: fieldMetaDataFieldkey,
            FieldMetaSort: localMetadata.length + 1,
        });
        setLocalMetadata(newMetadata);
        setNewMetaDataId(newMetaDataId - 1);
        setLocalMetadata(newMetadata);
    };

    return (
        <div>
            <div className="fieldedit-objects-header">
                <div style={{ flexDirection: "column", flex: "1" }}>
                    {occurs >= 1 && (
                        <span className="helper-text">This metadata is shared across {occurs} field(s).</span>
                    )}
                </div>
                <div className="fieldedit-objects-header-actions">
                    <Tooltip title={"Add new metadata values"}>
                        <div>
                            <Button
                                variant="contained"
                                color="secondary"
                                className={"add-button"}
                                startIcon={<i className="material-icons">add</i>}
                                disabled={!selectedFields || isSaving}
                                onClick={addMetadataRow}
                            >
                                Add
                            </Button>
                        </div>
                    </Tooltip>
                </div>
                {localMetadata && localMetadata.length >= 0 && (
                    <div className="fieldedit-objects-header-actions">
                        <Tooltip title={"Update the sort column based on alphabetical order of labels"}>
                            <div>
                                <IconButton
                                    onClick={sortMetaData}
                                    aria-label="re-sort metadata"
                                    disabled={!selectedFields || isSaving}
                                >
                                    <i className="material-icons">sort</i>
                                </IconButton>
                            </div>
                        </Tooltip>
                    </div>
                )}
            </div>
            <h4>
                {localMetadata && localMetadata.length > 0 ? (
                    renderMetadataTable()
                ) : (
                    <p className="placeholder">There is currently no metadata associated with this field.</p>
                )}
            </h4>
        </div>
    );
};

export default FieldMetaDataMain;
